import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  advancement as advApi,
  ApiError,
  dens as densApi,
  scouts as scoutsApi,
  type AdvancementAdventure,
  type DenRow,
  type ScoutAdvancement,
  type ScoutDetail as ScoutDetailT,
} from '../api';
import { Attribution } from '../components/Attribution';
import { useUnits } from '../units';

export function ScoutDetail() {
  const { unitId = '', scoutId = '' } = useParams();
  const { currentUnit, hasRole, councilCanView } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('DEN_LEADER') || councilCanView('advancement') || councilCanView('roster');

  const [scout, setScout] = useState<ScoutDetailT | null>(null);
  const [dens, setDens] = useState<DenRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    scoutsApi
      .get(unitId, scoutId)
      .then(setScout)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Not found'));
  }, [unitId, scoutId]);

  useEffect(() => {
    if (!mayView) return;
    load();
    densApi.list(unitId).then((r) => setDens(r.dens)).catch(() => setDens([]));
  }, [mayView, load, unitId]);

  if (!mayView) return <Navigate to="/" replace />;
  if (error) return <div className="panel"><p className="error">{error}</p></div>;
  if (!scout) return <p className="muted">Loading…</p>;

  return (
    <div className="panel">
      <p className="muted small">
        <Link to={`/units/${unitId}/roster`}>← Roster</Link> · {currentUnit?.name}
      </p>
      <h1>
        {scout.firstName} {scout.lastName}
      </h1>

      <EditScout unitId={unitId} scout={scout} onSaved={load} />

      <h2>Den</h2>
      <DenAssign unitId={unitId} scout={scout} dens={dens} onChanged={load} />

      <h2>Den history</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>Year</th>
            <th>Den</th>
            <th>From</th>
            <th>To</th>
          </tr>
        </thead>
        <tbody>
          {scout.denHistory.map((h, i) => (
            <tr key={i}>
              <td>{h.programYear}</td>
              <td>
                {h.denName}
                {h.current && <span className="badge" style={{ marginLeft: 8 }}>current</span>}
              </td>
              <td className="muted">{new Date(h.startedAt).toLocaleDateString()}</td>
              <td className="muted">{h.endedAt ? new Date(h.endedAt).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
          {scout.denHistory.length === 0 && (
            <tr>
              <td colSpan={4} className="muted">
                Never assigned to a den.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <h2>Advancement</h2>
      <Advancement unitId={unitId} scoutId={scout.id} canEdit={hasRole('ADMIN') || hasRole('DEN_LEADER')} />

      <h2>Guardians</h2>
      <Guardians unitId={unitId} scout={scout} onChanged={load} />
    </div>
  );
}

const CAT_LABEL: Record<string, string> = {
  required: 'Required',
  elective: 'Elective',
  special_elective: 'Shooting sports',
};

export function Advancement({
  unitId,
  scoutId,
  canEdit,
}: {
  unitId: string;
  scoutId: string;
  canEdit: boolean;
}) {
  const [data, setData] = useState<ScoutAdvancement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    advApi
      .get(unitId, scoutId)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load advancement'));
  }, [unitId, scoutId]);

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  if (!data.scout.rank) {
    return (
      <p className="muted">
        This Scout isn’t in a den with a rank set. Assign a den and set its rank (Dens page) to
        track advancement.
      </p>
    );
  }

  return (
    <>
      {data.summary.readyToAward && (
        <p className="callout ok">
          🎖️ Ready to award <strong>{data.summary.rankLabel}</strong> — all required adventures and{' '}
          {data.summary.electivesComplete} electives are complete.
        </p>
      )}
      <p className="muted small">
        {data.summary.requiredComplete}/{data.summary.requiredTotal} required adventures ·{' '}
        {data.summary.electivesComplete}/{data.summary.minElectives} electives ·{' '}
        {data.summary.adventuresComplete} total complete
      </p>
      {data.adventures.map((a) => (
        <AdventureProgress
          key={a.sourceId}
          adventure={a}
          onToggle={
            canEdit
              ? async (num, done) => {
                  setData(await advApi.set(unitId, scoutId, a.sourceId, { [num]: done }));
                }
              : undefined
          }
        />
      ))}
      <Attribution />
    </>
  );
}

function AdventureProgress({
  adventure: a,
  onToggle,
}: {
  adventure: AdvancementAdventure;
  onToggle?: (requirementNumber: string, done: boolean) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const pct = a.total ? Math.round((a.completedCount / a.total) * 100) : 0;

  return (
    <div className="adv-progress">
      <button className="adv-progress-head" onClick={() => setOpen((o) => !o)}>
        <span className={a.complete ? 'done' : ''}>{a.name}</span>
        <span className="muted small">
          {CAT_LABEL[a.category] ?? a.category} · {a.completedCount}/{a.total}
        </span>
        <span className="adv-bar">
          <span className="adv-bar-fill" style={{ width: `${pct}%` }} />
        </span>
      </button>
      {open && (
        <div className="adv-progress-body">
          {a.officialUrl && (
            <a href={a.officialUrl} target="_blank" rel="noreferrer" className="muted small">
              official guide ↗
            </a>
          )}
          {a.hasNoPublishedRequirements ? (
            <label className="adv-check">
              <input
                type="checkbox"
                checked={a.complete}
                disabled={!onToggle}
                onChange={(e) => onToggle?.('', e.target.checked)}
              />
              <span>
                {a.isShootingSports
                  ? 'Completed at an approved event (no requirement checklist)'
                  : 'Adventure completed (no requirements in the catalog)'}
              </span>
            </label>
          ) : (
            <ol className="adv-reqs">
              {a.requirements.map((r) => (
                <li key={r.number}>
                  <label className="adv-check">
                    <input
                      type="checkbox"
                      checked={r.completed}
                      disabled={!onToggle}
                      onChange={(e) => onToggle?.(r.number, e.target.checked)}
                    />
                    <span className={r.completed ? 'done' : ''}>
                      {r.text}
                      {r.viaMeeting && <span className="muted small"> · from a meeting</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ol>
          )}
          {a.textRights && <p className="muted small">{a.textRights}</p>}
        </div>
      )}
    </div>
  );
}

function EditScout({
  unitId,
  scout,
  onSaved,
}: {
  unitId: string;
  scout: ScoutDetailT;
  onSaved: () => void;
}) {
  const [firstName, setFirst] = useState(scout.firstName);
  const [lastName, setLast] = useState(scout.lastName);
  const [grade, setGrade] = useState(scout.grade ?? '');
  const [dob, setDob] = useState(scout.dob ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    firstName !== scout.firstName ||
    lastName !== scout.lastName ||
    grade !== (scout.grade ?? '') ||
    dob !== (scout.dob ?? '');

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await scoutsApi.update(unitId, scout.id, {
        firstName,
        lastName,
        grade: grade || null,
        dob: dob || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={save}>
      <input value={firstName} onChange={(e) => setFirst(e.target.value)} required />
      <input value={lastName} onChange={(e) => setLast(e.target.value)} required />
      <input placeholder="Grade" value={grade} onChange={(e) => setGrade(e.target.value)} style={{ minWidth: 70 }} />
      <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
      <button type="submit" className="primary" disabled={busy || !dirty}>
        {busy ? '…' : 'Save'}
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}

function DenAssign({
  unitId,
  scout,
  dens,
  onChanged,
}: {
  unitId: string;
  scout: ScoutDetailT;
  dens: DenRow[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <p>
      <select
        value={scout.den?.id ?? ''}
        disabled={busy}
        onChange={async (e) => {
          setError(null);
          setBusy(true);
          try {
            await scoutsApi.assignDen(unitId, scout.id, e.target.value || null);
            onChanged();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not change den');
          } finally {
            setBusy(false);
          }
        }}
      >
        <option value="">— unassigned —</option>
        {dens.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      {error && <span className="error"> {error}</span>}
    </p>
  );
}

function Guardians({
  unitId,
  scout,
  onChanged,
}: {
  unitId: string;
  scout: ScoutDetailT;
  onChanged: () => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await scoutsApi.addGuardian(unitId, scout.id, email);
      setEmail('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not link guardian');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <table className="grid">
        <tbody>
          {scout.guardians.map((g) => (
            <tr key={g.userId}>
              <td>{g.displayName}</td>
              <td>{g.email}</td>
              <td className="row-actions">
                <button
                  onClick={async () => {
                    await scoutsApi.removeGuardian(unitId, scout.id, g.userId);
                    onChanged();
                  }}
                >
                  Unlink
                </button>
              </td>
            </tr>
          ))}
          {scout.guardians.length === 0 && (
            <tr>
              <td className="muted">No guardians linked.</td>
            </tr>
          )}
        </tbody>
      </table>
      <form className="add-member" onSubmit={add}>
        <input
          type="email"
          placeholder="guardian@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" className="primary" disabled={busy}>
          {busy ? '…' : 'Link guardian'}
        </button>
        {error && <span className="error">{error}</span>}
        <p className="hint">
          They must already have an account; linking also gives them the Parent role in this unit.
        </p>
      </form>
    </>
  );
}
